import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { ADPResourceService, CustomFormControl } from "digital-core";
import { AccountService } from "src/app/core/services/account.service";
import { GoogleAnalyticsService } from "src/app/core/services/google-tag-manager.service";
import { SnackbarService } from "src/app/core/services/snackbar/snackbar.service";
import { ContractContactDetails, SendOTPRequest, VerifyOTPRequest } from "src/app/model/account/user-otp-request";
import { GTMOnboardingEvents, OnboardingSteps } from "src/app/model/google-tag-manager/api-status";

@Component({
  selector: 'app-verify-otp',
  templateUrl: './verify-otp.component.html',
  styleUrls: ['./verify-otp.component.scss'],
})
export class VerifyOTPComponent implements OnInit {
  @Input() otpRequest: SendOTPRequest;
  @Input() isReviewContactFinished: boolean;
  @Input() isSecondaryOTPNeeded: boolean;
  @Input() lookupResponse: any;
  @Input() isSecondaryMemberInvite = false;
  @Output() verifyOTPResponse = new EventEmitter();

  otpError: string;
  otpControl = new CustomFormControl();
  timerDisplay: any;
  isTimerVisible: boolean = false;
  userContact:string;
  invalidOTPRemainingCount:number;

  validateOtpForm = new UntypedFormGroup({
    otpControl: this.otpControl
  });

  constructor(
    private accountService: AccountService,
    private snackbarService: SnackbarService,
    private googleAnalyticsService: GoogleAnalyticsService,
    public router: Router,
    public resourceService: ADPResourceService
  ) { }

  ngOnInit() {
    this.userContact = this.otpRequest.maskedUserContact ?? this.otpRequest.userContact;
    this.invalidOTPRemainingCount = +this.resourceService.getPortalSetting("Signup_Create_Account_InvalidOTP_MaxAttempts").Value;
    this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.InProcess, OnboardingSteps.VerifyOTP);
  }

  onOTPChange() {
    this.otpError = undefined;
  }

  validateOtp(e) {
    this.otpError = undefined;
    let request: VerifyOTPRequest = {
      accessCode: this.otpControl.value,
      channel: this.otpRequest.channel,
      userContact: this.otpRequest.userContact,
      sessionId: !this.isReviewContactFinished && !this.isSecondaryOTPNeeded ? this.lookupResponse.sessionId : null,
    };

    // This is valid Only when the verify OTP is via secondary member invite screen flow
    if (this.isSecondaryMemberInvite) {
      request.sessionId = null;
    }
    
    this.accountService.validateOTP(request).subscribe({
      next: (response) => {
        if (response) {
          this.googleAnalyticsService.contractId = response.decryptedContractDetails?.contractId;
          this.googleAnalyticsService.isPrimary = true;
          this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Complete, OnboardingSteps.VerifyOTP);
          this.verifyOTPResponse.emit(response);
        }
      },
      error: (err) => {
        const emitError = {
          message: err?.error?.Message,
          type: err?.error?.ExceptionType,
          code: err?.error?.ErrorCode,
        }
        this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Failed, OnboardingSteps.VerifyOTP, emitError);
        if (err.error && err.error.ErrorCode == 'OTPExpired') {
          this.otpError = this.resourceService.getViewLabel('Signup_Create_Account_OTPExpired_Error');
        } else if (err.error && err.error.ErrorCode == 'InvalidOTPCode') {
          this.otpError = this.resourceService.getViewLabel('Signup_Create_Account_InvalidOTP_Error').replace('{0}', String(this.invalidOTPRemainingCount--));
        } else if (err.error && err.error.ErrorCode == 'SessionTimeout') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'Global_Error_Code_SessionTimeout' }});
        } else if (err.error && err.error.ErrorCode == 'InvalidPhoneNumber') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'Global_Error_Code_InvalidPhoneNumber' }});
        } else if (err.error && err.error.ErrorCode == 'MaxRetryCountReached') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_Signup_Create_Account_MaxRetryCountReached' }});
        } else {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_HavingTrouble' }});
        }
      }
    });
  }

  resendOTP() {
    this.otpError = undefined;
    this.accountService.sendOTPCode(this.otpRequest).subscribe({
      next: (response: any) => {
        // Note: To get the count of send attempts use "resp.sendAttempts";
        if (response && response.status) {
          this.snackbarService.showSimpleMessage(`${this.resourceService.getViewLabel('Signup_Create_Account_VerifyOTP_Resend_Success')} ${this.userContact}.`);
          this.validateOtpForm.reset();
          this.timer(1);
        }
      },
      error: (err) => {
        const emitError = {
          message: err?.error?.Message,
          type: err?.error?.ExceptionType,
          code: err?.error?.ErrorCode,
        }
        this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Failed, OnboardingSteps.VerifyOTP, emitError);
        if (err.error && err.error.ErrorCode == 'CreateOTP') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_Signup_Create_Account_CreateOTP' }});
        } else if (err.error && err.error.ErrorCode == 'SMSNotSupportedOnLandline') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_Signup_Create_Account_SMSNotSupportedOnLandline' }});
        } else if (err.error && err.error.ErrorCode == 'InvalidPhoneNumber') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'Global_Error_Code_InvalidPhoneNumber' }});
        } else if (err.error && err.error.ErrorCode == 'OTPSendAttemptReached') {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_Signup_Create_Account_OTPSendAttemptReached' }});
        } else {
          this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_HavingTrouble' }});
        }
      }
    });
  }

  timer(minute) {
    // let minute = 1;
    let seconds: number = minute * 30;
    let textSec: any = "0";
    let statSec: number = 30;

    const prefix = minute < 10 ? "0" : "";

    this.isTimerVisible = true;
    const timer = setInterval(() => {
      seconds--;
      if (statSec != 0) statSec--;
      else statSec = 59;

      if (statSec < 10) {
        textSec = "0" + statSec;
      } else textSec = statSec;

      this.timerDisplay = `${prefix}${Math.floor(seconds / 60)}:${textSec}`;

      if (seconds == 0) {
        this.isTimerVisible = false;
        this.timerDisplay = undefined;
        clearInterval(timer);
      }
    }, 1000);
  }
}
